import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { Outlet, useLocation, useNavigation } from 'react-router-dom';
import Header from './Header';
import MobileBottomTabs from './MobileBottomTabs';
import RouteLoading from './RouteLoading';
import Sidebar from './Sidebar';
import { useT } from '@/i18n';
import type { BreadcrumbResolver } from '../lib/breadcrumb';

interface AppLayoutProps {
  /** Optional override for the breadcrumb derivation. */
  breadcrumbResolver?: BreadcrumbResolver;
  /** Pre-resolved name for a dynamic route segment (e.g., a project name). */
  resolvedSegmentName?: string | null;
  /** Custom toast/notification root rendered at the bottom-right. */
  toaster?: ReactNode;
  /** When provided, replaces the default route loader. */
  routeFallback?: ReactNode;
  /** Optional content rendered above the routed page body. */
  contentTop?: ReactNode;
}

function NavProgress() {
  const navigation = useNavigation();
  if (navigation.state === 'idle') return null;
  return (
    <div className="absolute inset-x-0 top-0 z-20 h-1 overflow-hidden bg-[rgba(82,119,172,0.12)]">
      <div className="h-full w-1/3 animate-pulse rounded-full bg-[var(--ce-color-accent)]" />
    </div>
  );
}

/**
 * The full authenticated app shell. Mount this as the parent route element
 * (typically inside a `<RequireAuth>` wrapper) for everything under `/app`.
 */
export default function AppLayout({
  breadcrumbResolver,
  resolvedSegmentName = null,
  toaster,
  routeFallback,
  contentTop,
}: AppLayoutProps = {}) {
  const { t } = useT();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--ce-color-canvas)]">
      {/* Desktop persistent sidebar */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      <Dialog.Root open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/40 lg:hidden" />
          <Dialog.Popup
            id="mobile-navigation-drawer"
            aria-label={t('shell.navigationDrawer', { defaultValue: 'Navigation drawer' })}
            finalFocus={menuButtonRef}
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] shadow-[0_30px_80px_rgba(15,42,92,0.35)] lg:hidden"
          >
            <Sidebar onClose={() => setMobileMenuOpen(false)} />
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="relative flex flex-1 flex-col overflow-hidden">
        <NavProgress />
        <Header
          onOpenMenu={() => setMobileMenuOpen(true)}
          menuButtonRef={menuButtonRef}
          menuOpen={mobileMenuOpen}
          breadcrumbResolver={breadcrumbResolver}
          resolvedSegmentName={resolvedSegmentName}
        />
        <main className="flex-1 overflow-y-auto px-6 py-6 pb-24 lg:px-8 lg:pb-6">
          {contentTop}
          <Suspense fallback={routeFallback ?? <RouteLoading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <MobileBottomTabs onOpenMenu={() => setMobileMenuOpen(true)} />
      {toaster}
    </div>
  );
}
